// src/stateManagers/services/authService.ts
import axios from 'axios';
import Cookies from 'js-cookie';
import { API_BASE_URL } from '../../apiSetup/apiConfig';

const COOKIE_OPTIONS = {
  secure: process.env.NODE_ENV === 'production',
  sameSite: 'Lax' as const,
  path: '/',
};

export interface LoginResponse {
  status: string;
  message: string;
  token: string;
  refresh_token: string;
}

export interface OtpRequestResponse {
  status: string;
  message: string;
  expires_in_seconds?: number;
  resend_after_seconds?: number;
}

export interface SimpleResponse {
  status: string;
  message: string;
}

export const login = async (username: string, password: string) => {
  const response = await axios.post<LoginResponse>(`${API_BASE_URL}api/login-v2`, {
    username,
    password,
  });

  if (response.data.status !== 'success') {
    throw new Error(response.data.message || 'Login failed');
  }

  Cookies.set('accessToken', response.data.token, COOKIE_OPTIONS);
  Cookies.set('refreshToken', response.data.refresh_token, COOKIE_OPTIONS);

  return response;
};

// ─────────────────────────────────────────────────────────────────────────────
// Email OTP: login + forgot password (public endpoints, so plain axios)
// ─────────────────────────────────────────────────────────────────────────────

// Backend returns HTTP 200 + status:"error" for auth failures, and 400/429/503
// with { status, message } for the rest — normalise both into an Error(message).
const getErrorMessage = (err: unknown, fallback: string): string => {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { message?: string } | undefined;
    if (data?.message) return data.message;
    if (!err.response) return 'Network error. Please check your connection and try again.';
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
};

const postAuth = async <T extends SimpleResponse>(
  path: string,
  body: Record<string, unknown>,
  fallback: string,
): Promise<T> => {
  try {
    const response = await axios.post<T>(`${API_BASE_URL}${path}`, body, { timeout: 15000 });
    if (response.data.status !== 'success') {
      throw new Error(response.data.message || fallback);
    }
    return response.data;
  } catch (err) {
    throw new Error(getErrorMessage(err, fallback));
  }
};

/** Step 1 of OTP login: email a one-time code. `identifier` = username or email. */
export const requestLoginOtp = (identifier: string) =>
  postAuth<OtpRequestResponse>('api/login-otp/request', { identifier }, 'Failed to send OTP');

/** Step 2 of OTP login: verify the code. Stores tokens exactly like `login`. */
export const verifyLoginOtp = async (identifier: string, otp: string, deviceId?: string) => {
  const data = await postAuth<LoginResponse>(
    'api/login-otp/verify',
    { identifier, otp, ...(deviceId ? { device_id: deviceId } : {}) },
    'OTP verification failed',
  );

  Cookies.set('accessToken', data.token, COOKIE_OPTIONS);
  Cookies.set('refreshToken', data.refresh_token, COOKIE_OPTIONS);

  return data;
};

/** Step 1 of forgot password: email a one-time code. */
export const requestPasswordResetOtp = (identifier: string) =>
  postAuth<OtpRequestResponse>('api/forgot-password/request', { identifier }, 'Failed to send OTP');

/** Step 2 of forgot password: verify the code and set the new password in one call. */
export const resetPasswordWithOtp = (identifier: string, otp: string, newPassword: string) =>
  postAuth<SimpleResponse>(
    'api/forgot-password/reset',
    { identifier, otp, new_password: newPassword },
    'Password reset failed',
  );

export const logout = async (_accessToken: string, _refreshToken: string) => {
  // No /api/logout route registered yet — clearing cookies client-side only.
  // Swap this for a real API call once the backend adds the endpoint.
  Cookies.remove('accessToken', COOKIE_OPTIONS);
  Cookies.remove('refreshToken', COOKIE_OPTIONS);
};

export const logoutAllDevices = async (_accessToken: string) => {
  // Same caveat — no backend route for this yet.
  Cookies.remove('accessToken', COOKIE_OPTIONS);
  Cookies.remove('refreshToken', COOKIE_OPTIONS);
};

// // src/stateManagers/services/authService.ts
// import axios from 'axios';
// import Cookies from 'js-cookie';
// import { API_BASE_URL } from '../../apiSetup/apiConfig';

// const COOKIE_OPTIONS = {
//   secure: process.env.NODE_ENV === 'production',
//   sameSite: 'Lax' as const,
//   path: '/',
// };

// export interface LoginResponse {
//   status: string;
//   message: string;
//   token: string;
//   refresh_token: string;
// }

// export const login = async (username: string, password: string) => {
//   const response = await axios.post<LoginResponse>(`${API_BASE_URL}api/login-v2`, {
//     username,
//     password,
//   });

//   if (response.data.status !== 'success') {
//     throw new Error(response.data.message || 'Login failed');
//   }

//   Cookies.set('accessToken', response.data.token, COOKIE_OPTIONS);
//   Cookies.set('refreshToken', response.data.refresh_token, COOKIE_OPTIONS);

//   return response;
// };

// export const logout = async (_accessToken: string, _refreshToken: string) => {
//   // No /api/logout route registered yet — clearing cookies client-side only.
//   // Swap this for a real API call once the backend adds the endpoint.
//   Cookies.remove('accessToken', COOKIE_OPTIONS);
//   Cookies.remove('refreshToken', COOKIE_OPTIONS);
// };

// export const logoutAllDevices = async (_accessToken: string) => {
//   // Same caveat — no backend route for this yet.
//   Cookies.remove('accessToken', COOKIE_OPTIONS);
//   Cookies.remove('refreshToken', COOKIE_OPTIONS);
// };